/**
 * Customer flower price display — promo strikethrough, sale price, optional badge (PBI #1234).
 *
 * All sale math is resolved server-side; pass {@link FlowerDisplayPricing} from
 * {@link resolveFlowerDisplayPricing}. Not for subscription tier pricing — see PriceDisplay.
 *
 * Sale styling matches {@link PriceDisplay} member strikethrough rows: muted gray
 * original first, bold sale amount second (standard was/now order), green savings badge.
 */

import { Badge, Group, Stack, Text, rem, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { brandColors, semanticColors } from '../../config/theme';
import { formatUsd, formatUsdFrom } from '../../utils/formatUsd';
import type { FlowerDisplayPricing } from '../../utils/resolveFlowerDisplayPricing';

/** Compact tile savings callout — matches {@link PriceDisplay} / mailed-card carousel. */
const COMPACT_PROMO_CALLOUT_FONT_SIZE = rem(10);

/** Image-overlay badge type on compact shop tiles ({@link FlowerPreviewTile} size="md"). */
const OVERLAY_BADGE_FONT_SIZE = rem(12);

/**
 * Two-column mobile shop tiles are too narrow for 12px "Labor Day Sale - 10% off".
 * Smaller type prefers a single line; genuinely long admin headlines still wrap.
 */
const OVERLAY_BADGE_FONT_SIZE_MOBILE = rem(10);

const OVERLAY_BADGE_FONT_SIZE_DEFAULT = rem(11);

export type FlowerPriceDisplayVariant = 'compactTile' | 'inline' | 'hero';

export interface FlowerPriceDisplayProps {
  pricing: FlowerDisplayPricing;
  variant: FlowerPriceDisplayVariant;
  /** Prefix for catalog tiles (e.g. "from" → "from $62.95"). */
  prefix?: 'from';
  /** When false, omit inline badge (caller may use FlowerPromoOffBadge on image). */
  showBadge?: boolean;
  /** Mantine Text size for the dominant (sale or list) amount. */
  size?: string;
}

/**
 * Compact "% off" label for carousel text bands (Members pay parity).
 */
export function formatFlowerPromoOffLabel(discountPercent: number): string {
  return `${discountPercent}% off`;
}

/**
 * Image-overlay badge copy: optional admin headline plus percent off
 * (e.g. "Labor Day Sale - 10% off").
 */
export function formatFlowerPromoOverlayBadgeLabel(
  discountPercent: number,
  publicHeadline?: string | null,
): string {
  const offLabel = formatFlowerPromoOffLabel(discountPercent);
  const headline = publicHeadline?.trim();
  if (!headline) {
    return offLabel;
  }

  return `${headline} - ${offLabel}`;
}

/**
 * Compact promo badge for flower sales (PBI #1234).
 * Used in carousel image overlays; pair with {@link formatFlowerPromoOffLabel}
 * in the tile text band for the same promo.
 */
export function FlowerPromoOffBadge({
  discountPercent,
  promoHeadline,
  size = 'sm',
}: {
  discountPercent: number;
  promoHeadline?: string | null;
  size?: 'xs' | 'sm' | 'md';
}) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const isOverlayMd = size === 'md';
  const isMobileOverlay = Boolean(isMobile && isOverlayMd);
  const label = formatFlowerPromoOverlayBadgeLabel(discountPercent, promoHeadline);
  const headline = promoHeadline?.trim();
  const ariaLabel = headline
    ? `${headline}, ${discountPercent} percent off`
    : `${discountPercent} percent off`;

  return (
    <Badge
      size={size}
      color={brandColors.success}
      variant="light"
      tt="none"
      aria-label={ariaLabel}
      data-testid="flower-promo-off-badge"
      styles={{
        root: {
          paddingLeft: isMobileOverlay ? rem(6) : rem(8),
          paddingRight: isMobileOverlay ? rem(6) : rem(8),
          paddingTop: isMobileOverlay ? rem(2) : rem(4),
          paddingBottom: isMobileOverlay ? rem(2) : rem(4),
          maxWidth: '100%',

          // Hug typical headlines on mobile so the pill is not a full-width
          // two-line bar; long copy still grows to 100% and wraps.
          width: isMobileOverlay ? 'auto' : '100%',
          height: 'auto',
          whiteSpace: 'normal',
          alignItems: 'flex-start',

          // 20% transparency (80% opaque mint) — almost solid; border defines the edge on photos.
          backgroundColor: 'color-mix(in srgb, var(--mantine-color-green-0) 80%, transparent)',
          backgroundImage: 'none',
          border: `1px solid ${semanticColors.border.success}`,
          boxShadow: '0 1px 4px rgba(15, 23, 42, 0.12)',
        },
        label: {
          fontSize: isMobileOverlay
            ? OVERLAY_BADGE_FONT_SIZE_MOBILE
            : isOverlayMd
              ? OVERLAY_BADGE_FONT_SIZE
              : OVERLAY_BADGE_FONT_SIZE_DEFAULT,
          fontWeight: 700,
          textTransform: 'none',
          whiteSpace: 'normal',
          textAlign: 'left',
          lineHeight: 1.25,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          color: 'var(--mantine-color-green-9)',
        },
      }}
    >
      {label}
    </Badge>
  );
}

/**
 * Formats the primary displayed amount, optionally with a "from" prefix.
 */
function formatPrimaryAmount(displayUsd: number, prefix?: 'from'): string {
  if (prefix === 'from') {
    return formatUsdFrom(displayUsd);
  }
  return formatUsd(displayUsd);
}

/**
 * Shared flower promo / vendor-sale price UI.
 */
export function FlowerPriceDisplay({
  pricing,
  variant,
  prefix,
  showBadge = true,
  size,
}: FlowerPriceDisplayProps) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const badge =
    showBadge && pricing.hasSale && pricing.discountPercent != null
      ? (
        <FlowerPromoOffBadge
          discountPercent={pricing.discountPercent}
          promoHeadline={pricing.promoHeadline}
          size={variant === 'hero' ? 'sm' : 'xs'}
        />
      )
      : null;

  if (!pricing.hasSale) {
    const textSize = size ?? (variant === 'hero' ? 'xl' : variant === 'inline' ? 'sm' : 'xs');
    return (
      <Text size={textSize} c={semanticColors.text.secondary} fw={variant === 'hero' ? 700 : 500}>
        {formatPrimaryAmount(pricing.listUsd, prefix)}
      </Text>
    );
  }

  if (variant === 'hero') {
    const heroSize = size ?? 'xl';
    return (
      <Stack gap="xs" align={isMobile ? 'flex-start' : undefined}>
        <Group gap="sm" wrap="wrap" align="baseline">
          <Text
            component="span"
            size="md"
            td="line-through"
            c={semanticColors.text.secondary}
            fw={400}
            aria-label={`Original price ${formatUsd(pricing.listUsd)}`}
          >
            {formatUsd(pricing.listUsd)}
          </Text>
          <Text
            size={heroSize}
            fw={700}
            c={semanticColors.text.title}
            aria-label={`Sale price ${formatUsd(pricing.saleUsd!)}`}
          >
            {formatUsd(pricing.saleUsd!)}
          </Text>
          {badge}
        </Group>
      </Stack>
    );
  }

  if (variant === 'inline') {
    const inlineSize = size ?? 'sm';
    return (
      <Group gap={6} wrap="wrap" align="baseline">
        <Text
          component="span"
          size="xs"
          td="line-through"
          c={semanticColors.text.secondary}
          aria-label={`Original price ${formatUsd(pricing.listUsd)}`}
        >
          {formatUsd(pricing.listUsd)}
        </Text>
        <Text
          size={inlineSize}
          fw={700}
          c={semanticColors.text.title}
          aria-label={`Sale price ${formatUsd(pricing.saleUsd!)}`}
        >
          {formatUsd(pricing.saleUsd!)}
        </Text>
        {badge}
      </Group>
    );
  }

  // compactTile — struck list + sale on the left, green "% off" on the right (mailed-card parity).
  // Image overlays may repeat the same promo via FlowerPromoOffBadge.
  const compactSize = size ?? 'xs';
  const promoCallout =
    pricing.hasSale && pricing.discountPercent != null
      ? formatFlowerPromoOffLabel(pricing.discountPercent)
      : null;

  return (
    <Group
      gap={4}
      wrap="nowrap"
      justify="space-between"
      align="baseline"
      style={{ width: '100%' }}
    >
      <Group gap={4} wrap="nowrap" align="baseline" style={{ flexShrink: 0 }}>
        <Text
          component="span"
          size="xs"
          td="line-through"
          c={semanticColors.text.secondary}
          style={{ flexShrink: 0 }}
          aria-label={`Original price ${formatUsd(pricing.listUsd)}`}
        >
          {formatUsd(pricing.listUsd)}
        </Text>
        <Text
          size={compactSize}
          fw={700}
          c={semanticColors.text.title}
          style={{ flexShrink: 0 }}
          aria-label={`Sale price ${formatPrimaryAmount(pricing.saleUsd!, prefix)}`}
        >
          {formatPrimaryAmount(pricing.saleUsd!, prefix)}
        </Text>
      </Group>
      {promoCallout ? (
        <Text
          fz={COMPACT_PROMO_CALLOUT_FONT_SIZE}
          fw={600}
          c={brandColors.success}
          ta="right"
          lineClamp={1}
          aria-label={promoCallout}
          style={{ flex: '1 1 auto', minWidth: 0 }}
          component="span"
        >
          {promoCallout}
        </Text>
      ) : null}
    </Group>
  );
}
